if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.initVariantMediaGrouping();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.variantMediaGroupUnsub?.();
        this.qbListenerTarget?.removeEventListener('change', this.qbChangeHandler);
      }

      initVariantMediaGrouping() {
        this.groupOptionPosition = parseInt(this.dataset.groupMediaPosition || '', 10);
        if (Number.isNaN(this.groupOptionPosition)) return;

        const mapEl = this.querySelector('[data-variant-media-colors]');
        if (!mapEl) return;
        try {
          this.mediaColorMap = JSON.parse(mapEl.textContent);
        } catch (error) {
          return;
        }

        const variantColorEl = this.querySelector('[data-variant-color-by-id]');
        if (variantColorEl) {
          try {
            this.variantColorById = JSON.parse(variantColorEl.textContent);
          } catch (error) {
            this.variantColorById = null;
          }
        }

        const productInfo = this.closest('product-info');

        // Offer radios contain package IDs, not variant IDs. Read their selected
        // color swatch first, then fall back to the server-rendered variant.
        let initialColor = this.getGroupedSelectedColor();
        if (initialColor == null && this.variantColorById) {
          const swatch = productInfo?.querySelector('.qb-swatch[aria-checked="true"]');
          const checked = productInfo?.querySelector(
            '.quantity-breaks-container[data-mode="variant"] input[name="quantity_break"]:checked'
          );
          const variantId = swatch?.dataset.variantId || checked?.value || this.dataset.selectedVariantId;
          initialColor = this.variantColorById[variantId];
        }
        if (initialColor != null) this.filterMediaByColor(initialColor);

        // Re-filter whenever Dawn's variant picker changes.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantMediaGroupUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const infoSectionId = productInfo?.dataset.originalSection || productInfo?.dataset.section;
            if (event?.data?.sectionId && infoSectionId && event.data.sectionId !== infoSectionId) return;

            const variant = event?.data?.variant;
            if (!variant || !variant.options) return;
            this.filterMediaByColor(variant.options[this.groupOptionPosition - 1]);
          });
        }

        // Re-filter when a custom radio selector (e.g. the quantity/package cards) changes.
        // These selectors bypass Dawn's variantChange, so we listen for their radio directly.
        if (productInfo && this.variantColorById) {
          this.qbListenerTarget = document;
          this.qbChangeHandler = (event) => {
            const target = event.target;
            if (!target || target.name !== 'quantity_break') return;
            if (!productInfo.contains(target)) return;
            const color = this.variantColorById[target.value];
            if (color != null) this.filterMediaByColor(color);
          };
          document.addEventListener('change', this.qbChangeHandler);
        }
      }

      getGroupedSelectedColor() {
        const selectedVariantEl = this.closest('product-info')?.querySelector(
          'variant-selects [data-selected-variant]'
        );
        if (!selectedVariantEl) return null;
        try {
          const variant = JSON.parse(selectedVariantEl.textContent);
          if (!variant || !variant.options) return null;
          return variant.options[this.groupOptionPosition - 1];
        } catch (error) {
          return null;
        }
      }

      filterMediaByColor(color) {
        if (!this.mediaColorMap || color == null) return;

        const map = this.mediaColorMap;
        const viewer = this.elements.viewer;
        const thumbnails = this.elements.thumbnails;
        if (!viewer) return;

        const viewerItems = Array.from(viewer.querySelectorAll('[data-media-id]'));

        // If this color has no dedicated media, fall back to showing everything
        // so the gallery is never left empty.
        const colorHasMedia = viewerItems.some((item) => {
          const colors = map[item.dataset.mediaId];
          return colors && colors.indexOf(color) !== -1;
        });

        const isVisible = (id) => {
          if (!colorHasMedia) return true;
          const colors = map[id];
          if (!colors) return true; // shared media not tied to a variant
          return colors.indexOf(color) !== -1;
        };

        viewerItems.forEach((item) => {
          item.classList.toggle('is-group-hidden', !isVisible(item.dataset.mediaId));
        });

        if (thumbnails) {
          thumbnails.querySelectorAll('[data-target]').forEach((item) => {
            item.classList.toggle('is-group-hidden', !isVisible(item.dataset.target));
          });
        }

        const sectionId = this.id.replace('MediaGallery-', '');
        const modal = document.getElementById(`ProductModal-${sectionId}`);
        const modalContent = modal?.querySelector('[role="document"]');
        if (modalContent) {
          modalContent.querySelectorAll(':scope > [data-media-id]').forEach((slide) => {
            slide.classList.toggle('is-group-hidden', !isVisible(`${sectionId}-${slide.dataset.mediaId}`));
          });
          if (modal.hasAttribute('open') && typeof modal.goTo === 'function') modal.goTo(0, 'instant');
        }

        // Recompute slider pages/counter now that item visibility changed.
        viewer.resetPages?.();
        thumbnails?.resetPages?.();

        // Make sure the large image belongs to the selected color: prefer a
        // color-specific media, then any visible one. Leave it alone when the
        // color has no dedicated media (unless the active one got hidden).
        const activeItem = viewer.querySelector('[data-media-id].is-active:not(.is-group-hidden)');
        const activeColors = activeItem ? map[activeItem.dataset.mediaId] : null;
        const activeIsColorSpecific = !!activeColors && activeColors.indexOf(color) !== -1;
        if (!activeItem || (colorHasMedia && !activeIsColorSpecific)) {
          let target = viewerItems.find(
            (item) =>
              !item.classList.contains('is-group-hidden') &&
              map[item.dataset.mediaId] &&
              map[item.dataset.mediaId].indexOf(color) !== -1
          );
          if (!target) target = viewer.querySelector('[data-media-id]:not(.is-group-hidden)');
          if (target) this.setActiveMedia(target.dataset.mediaId, false, true);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, keepPageScroll) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(.is-group-hidden)');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (keepPageScroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
